import { GameEngine } from "~/engine/instance";
import type { Vector2 } from "~/state/game-types";
import type { TerrainGeneratorConfig } from "~/terrain/terrain-generation";
import { TerrainGeneratorClient } from "~/terrain/terrain-generation-client";
import {
  TERRAIN_TYPES,
  TERRAIN_TYPES_MAP,
  VOXEL_EMPTY,
  type TerrainType,
} from "~/terrain/types";
import { VoxelBuffer } from "~/terrain/voxel-buffer";
import { DirtyRect } from "~/utils/dirty-rect";
import { pairingFunction } from "~/utils/pairing";
import { pointIntersectsPill } from "~/utils/shape-intersections";

/**
 * Represents a single voxel in the terrain
 */
export type Voxel = {
  uid: number;
  indexInChunk: number;
  voxelPos: Vector2; // global voxel position
  chunk: VoxelChunk;
  terrainType: number; // 0 = empty, 1+ = solid terrain types or minerals
  terrainInfo: TerrainType;
};

/**
 * Represents a chunk of the voxel terrain for efficient storage and rendering
 */
export class VoxelChunk {
  voxelStructs: VoxelBuffer | null = null;

  loadPromise: Promise<void> | null = null;

  needsDestructionDecay = false;

  needsRenderRect = new DirtyRect({
    widthForIndexes: GameEngine.voxelTerrain.chunkWidthInVoxels,
    onWrite: () => {
      this._isAllAirOrLiquid = null;
    },
  });

  constructor(
    readonly id: number,
    /** position in the chunk grid */
    readonly chunkPlacement: Vector2,
  ) {}

  voxelAt(index: number): VoxelBuffer {
    // edge case: chunk not loaded yet
    if (this.voxelStructs == null) {
      return GameEngine.voxelTerrain.BLANK_VOXEL_BUFFER;
    }

    return this.voxelStructs.at(index);
  }

  private _isAllAirOrLiquid: boolean | null = null;
  isAllAirOrLiquid(): boolean {
    if (this._isAllAirOrLiquid != null) {
      return this._isAllAirOrLiquid;
    }
    if (this.voxelStructs == null) {
      throw new Error("chunk voxelStructs not loaded");
    }
    const len = this.voxelStructs.count;
    const typesBuffer = this.voxelStructs.buffers.terrainType;
    let terrainType: number;

    for (let i = len - 1; i >= 0; i--) {
      terrainType = typesBuffer[i]!;
      if (
        terrainType !== VOXEL_EMPTY &&
        terrainType !== TERRAIN_TYPES.water &&
        terrainType !== TERRAIN_TYPES.smoke
      ) {
        this._isAllAirOrLiquid = false;
        return false;
      }
    }
    this._isAllAirOrLiquid = true;
    return true;
  }
}

// chunk ID to chunk data
export type ChunkMap = Map<number, VoxelChunk>;

/**
 * Configuration for the voxel terrain
 */
export type VoxelTerrainConfig = {
  /** Size of each voxel in world units */
  voxelSize: number;
} & TerrainGeneratorConfig;

/**
 * Manages voxel-based terrain in the game
 */
export class VoxelTerrain {
  public chunks: ChunkMap = new Map();

  private terrainGeneratorClient: TerrainGeneratorClient;

  public voxelSize: number;
  public chunkWidthInVoxels: number;
  public chunkHeightInVoxels: number;
  // public worldWidthInChunks: number;
  public worldHeightInChunks: number;
  public voxelsPerChunk: number;
  public chunkWidth: number;
  public chunkHeight: number;
  // public worldWidth: number;
  public worldHeight: number;
  // public worldWidthInVoxels: number;
  public worldHeightInVoxels: number;

  public BLANK_VOXEL_BUFFER: VoxelBuffer;

  constructor(config: VoxelTerrainConfig) {
    this.voxelSize = config.voxelSize;
    this.chunkWidthInVoxels = config.chunkWidthInVoxels;
    this.chunkHeightInVoxels = config.chunkHeightInVoxels;
    // this.worldWidthInChunks = config.worldWidthInChunks;
    this.worldHeightInChunks = config.worldHeightInChunks;
    this.voxelsPerChunk = this.chunkWidthInVoxels * this.chunkHeightInVoxels;
    this.chunkWidth = this.chunkWidthInVoxels * this.voxelSize;
    this.chunkHeight = this.chunkHeightInVoxels * this.voxelSize;
    // this.worldWidth = this.worldWidthInChunks * this.chunkWidthInVoxels * this.voxelSize;
    this.worldHeight =
      this.worldHeightInChunks * this.chunkHeightInVoxels * this.voxelSize;
    // this.worldWidthInVoxels = this.worldWidthInChunks * this.chunkWidthInVoxels;
    this.worldHeightInVoxels =
      this.worldHeightInChunks * this.chunkHeightInVoxels;

    this.terrainGeneratorClient = new TerrainGeneratorClient(config);

    this.BLANK_VOXEL_BUFFER = new VoxelBuffer(this.voxelsPerChunk);
  }

  /**
   * Get unique chunk ID (integer) from chunk coordinates
   */
  public getChunkId(chunkX: number, chunkY: number): number {
    return pairingFunction(chunkX, chunkY);
  }

  /**
   * Convert world position to voxel coordinates
   */
  public worldToVoxel(
    /** world position is floats */
    worldPos: Vector2,
  ): Vector2 {
    return {
      x: Math.floor(worldPos.x / this.voxelSize),
      y: Math.floor(worldPos.y / this.voxelSize),
    };
  }

  public voxelToChunk(
    /** voxel position is integers */
    voxelPos: Vector2,
  ): Vector2 {
    return {
      x: Math.floor(voxelPos.x / this.chunkWidthInVoxels),
      y: Math.floor(voxelPos.y / this.chunkWidthInVoxels),
    };
  }
  public worldToChunk(worldPos: Vector2): Vector2 {
    return this.voxelToChunk(this.worldToVoxel(worldPos));
  }

  /**
   * Convert voxel coordinates to world position
   */
  public voxelToWorld(voxel: Vector2): Vector2 {
    return {
      x: voxel.x * this.voxelSize,
      y: voxel.y * this.voxelSize,
    };
  }

  public chunkToWorld(chunkPlacement: Vector2): Vector2 {
    return {
      x: chunkPlacement.x * this.chunkWidthInVoxels * this.voxelSize,
      y: chunkPlacement.y * this.chunkHeightInVoxels * this.voxelSize,
    };
  }

  /**
   * Get or create a chunk at the specified coordinates
   */
  private getOrCreateChunk<Wait extends boolean = false>(
    chunkX: number,
    chunkY: number,
    wait: Wait = false as Wait,
  ) {
    type Return = Wait extends true ? MaybePromise<VoxelChunk> : VoxelChunk;

    const chunkId = this.getChunkId(chunkX, chunkY);

    let chunk = this.chunks.get(chunkId);
    if (chunk) {
      if (chunk.loadPromise == null || !wait) {
        return chunk as Return;
      }

      return chunk.loadPromise.then(() => chunk) as Return;
    }

    let remoteChunk;
    if (
      (remoteChunk = GameEngine.gameStore.getState().initialChunks[chunkId])
    ) {
      console.log(`Loaded remote chunk: ${chunkX},${chunkY}`);
      chunk = new VoxelChunk(chunkId, {
        x: chunkX,
        y: chunkY,
      });
      chunk.voxelStructs = new VoxelBuffer(remoteChunk.terrain_types);
      this.chunks.set(chunkId, chunk);
      return chunk as Return;
    }

    // Create new chunk
    chunk = new VoxelChunk(chunkId, {
      x: chunkX,
      y: chunkY,
    });
    this.chunks.set(chunkId, chunk);

    // Generate terrain for this chunk
    chunk.loadPromise = this.terrainGeneratorClient
      .generateChunkTerrainAsync(chunk.chunkPlacement)
      .then((terrainTypesBuffer) => {
        chunk.voxelStructs = new VoxelBuffer(terrainTypesBuffer);

        chunk.loadPromise = null;
      });

    if (!wait) {
      return chunk as Return;
    }

    return chunk.loadPromise.then(() => chunk) as Return;
  }

  /**
   * Get the highest voxel at a specific X position.
   * Throw an error if somehow there's no terrain at this x (should never happen).
   */
  public async getMaxVoxelY(voxelX: number): Promise<Voxel> {
    // starting from the world height + 1 chunk height padding, go down until we find a solid voxel
    for (
      let y =
        this.worldHeightInVoxels + Math.floor(this.chunkHeightInVoxels / 2);
      y >= 0;
      y--
    ) {
      const voxel = await this.getVoxelAtAsync({ x: voxelX, y });
      if (voxel != null) {
        const voxelAbove = await this.getVoxelAtAsync({
          x: voxelX,
          y: y + 1,
        });
        if (voxelAbove != null) {
          throw new Error(`Expected no solid voxel above`);
        }

        return voxel;
      }
    }
    throw new Error(`No terrain found at voxelX: ${voxelX}`);
  }

  private async getVoxelAtAsync(voxelPos: Vector2): Promise<Voxel | null> {
    const chunkPos = this.voxelToChunk(voxelPos);
    const chunk = await this.getOrCreateChunk(chunkPos.x, chunkPos.y, true);
    return this.getVoxelAt(voxelPos, chunk);
  }

  /**
   * Get the voxel at a specific voxel position
   */
  public getVoxelInfoAt(
    voxelPos: Vector2,
    chunk?: VoxelChunk,
  ): WithNullable<Voxel, "terrainInfo"> {
    if (!chunk) {
      const { x: chunkX, y: chunkY } = this.voxelToChunk(voxelPos);

      // Get the chunk
      chunk = this.getOrCreateChunk(chunkX, chunkY);
    }

    // Get local coordinates within chunk
    let localX = voxelPos.x % this.chunkWidthInVoxels;
    let localY = voxelPos.y % this.chunkHeightInVoxels;
    if (localX < 0) localX += this.chunkWidthInVoxels;
    if (localY < 0) localY += this.chunkHeightInVoxels;

    // Get voxel from chunk
    const index = localY * this.chunkWidthInVoxels + localX;
    const voxel = chunk.voxelAt(index);

    const terrainType = voxel.terrainType;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (terrainType === undefined) {
      throw new Error(
        `no terrainType for index=${index} chunk=${chunk.chunkPlacement.x},${chunk.chunkPlacement.y}`,
      );
    }

    let terrainInfo: TerrainType | null;
    if (terrainType !== VOXEL_EMPTY) {
      terrainInfo = TERRAIN_TYPES_MAP[terrainType]!;
    } else {
      terrainInfo = null;
    }

    return {
      uid: voxel.uid,
      indexInChunk: index,
      voxelPos,
      terrainType,
      terrainInfo,
      chunk,
    };
  }

  public getVoxelAt(voxelPos: Vector2, chunk?: VoxelChunk): Voxel | null {
    const voxel = this.getVoxelInfoAt(voxelPos, chunk);
    if (voxel.terrainType === VOXEL_EMPTY) {
      return null;
    }
    return voxel as Voxel;
  }

  /**
   * Get all voxels within a line segment. Like a pill shape.
   * This line's thickness is radius * 2.
   * The line has rounded caps of the given radius.
   */
  public *getVoxelsInLineRoundedCaps(
    startWorldPos: Vector2,
    endWorldPos: Vector2,
    radius: number,
  ): Generator<Voxel> {
    // Calculate the line vector
    const lineVector = {
      x: endWorldPos.x - startWorldPos.x,
      y: endWorldPos.y - startWorldPos.y,
    };
    const lineLength = Math.sqrt(
      lineVector.x * lineVector.x + lineVector.y * lineVector.y,
    );

    // If the line is very short, treat it as a circle
    if (lineLength < 0.001) {
      yield* this.getVoxelsInRadius(startWorldPos, radius);
      return;
    }

    // Normalize the line vector
    const normalizedLineVector = {
      x: lineVector.x / lineLength,
      y: lineVector.y / lineLength,
    };

    // Calculate the perpendicular vector (for the rectangle part)
    const perpVector = {
      x: -normalizedLineVector.y,
      y: normalizedLineVector.x,
    };

    // Calculate the bounding box that encompasses the entire pill shape
    const minX = Math.min(startWorldPos.x, endWorldPos.x) - radius;
    const maxX = Math.max(startWorldPos.x, endWorldPos.x) + radius;
    const minY = Math.min(startWorldPos.y, endWorldPos.y) - radius;
    const maxY = Math.max(startWorldPos.y, endWorldPos.y) + radius;

    // Convert to voxel coordinates
    const minVoxelX = Math.floor(minX / this.voxelSize);
    const maxVoxelX = Math.floor(maxX / this.voxelSize);
    const minVoxelY = Math.floor(minY / this.voxelSize);
    const maxVoxelY = Math.floor(maxY / this.voxelSize);

    // Check each voxel in the bounding box
    for (let voxelY = minVoxelY; voxelY <= maxVoxelY; voxelY++) {
      for (let voxelX = minVoxelX; voxelX <= maxVoxelX; voxelX++) {
        // Calculate voxel center
        const voxelCenterX = (voxelX + 0.5) * this.voxelSize;
        const voxelCenterY = (voxelY + 0.5) * this.voxelSize;

        // Check if this voxel intersects with the pill shape
        if (
          pointIntersectsPill(
            voxelCenterX,
            voxelCenterY,
            startWorldPos,
            endWorldPos,
            radius,
            lineLength,
            normalizedLineVector,
            perpVector,
          )
        ) {
          const voxelPos = { x: voxelX, y: voxelY };
          const voxel = this.getVoxelAt(voxelPos);
          if (voxel != null) {
            yield voxel;
          }
        }
      }
    }
  }

  /**
   * Get all voxels within a radius of a position
   */
  public *getVoxelsInRadius(
    worldPos: Vector2,
    radius: number,
  ): Generator<Voxel> {
    // Calculate the bounding box in world coordinates
    const minWorldX = worldPos.x - radius;
    const maxWorldX = worldPos.x + radius;
    const minWorldY = worldPos.y - radius;
    const maxWorldY = worldPos.y + radius;

    // Convert to voxel coordinates
    const minVoxelX = Math.floor(minWorldX / this.voxelSize);
    const maxVoxelX = Math.floor(maxWorldX / this.voxelSize);
    const minVoxelY = Math.floor(minWorldY / this.voxelSize);
    const maxVoxelY = Math.floor(maxWorldY / this.voxelSize);

    // Check each voxel in the bounding box
    for (let voxelY = minVoxelY; voxelY <= maxVoxelY; voxelY++) {
      for (let voxelX = minVoxelX; voxelX <= maxVoxelX; voxelX++) {
        // Calculate voxel rectangle bounds (bottom-left coordinate system)
        const voxelLeft = voxelX * this.voxelSize;
        const voxelRight = (voxelX + 1) * this.voxelSize;
        const voxelBottom = voxelY * this.voxelSize;
        const voxelTop = (voxelY + 1) * this.voxelSize;

        // Find closest point on voxel rectangle to circle center
        const closestX = Math.max(voxelLeft, Math.min(worldPos.x, voxelRight));
        const closestY = Math.max(voxelBottom, Math.min(worldPos.y, voxelTop));

        // Calculate distance from circle center to closest point
        const dx = worldPos.x - closestX;
        const dy = worldPos.y - closestY;
        const distanceSquared = dx * dx + dy * dy;

        // Check if circle intersects with voxel rectangle
        if (distanceSquared <= radius * radius) {
          const voxelPos = { x: voxelX, y: voxelY };
          const voxel = this.getVoxelAt(voxelPos);
          if (voxel != null) {
            yield voxel;
          }
        }
      }
    }
  }

  public *getVoxelsInRect(
    minWorldPos: Vector2,
    maxWorldPos: Vector2,
    withSolids: boolean | null = null,
  ): Generator<Voxel> {
    const minVoxel = this.worldToVoxel(minWorldPos);
    // Subtract small epsilon to handle boundary cases correctly
    // This prevents including adjacent voxels when the bounding box edge
    // is exactly on a voxel boundary
    const maxVoxel = this.worldToVoxel({
      x: maxWorldPos.x - 0.0001,
      y: maxWorldPos.y - 0.0001,
    });

    for (let voxelX = minVoxel.x; voxelX <= maxVoxel.x; voxelX++) {
      for (let voxelY = minVoxel.y; voxelY <= maxVoxel.y; voxelY++) {
        const voxelPos = { x: voxelX, y: voxelY };
        const voxel = this.getVoxelAt(voxelPos);
        if (voxel != null) {
          if (
            withSolids != null &&
            withSolids !== (voxel.terrainInfo.movementDrag == null)
          ) {
            continue;
          }
          yield voxel;
        }
      }
    }
  }

  /**
   * Get chunks in a rectangular area
   */
  public async loadChunksInRect(
    minWorldPos: Vector2,
    maxWorldPos: Vector2,
  ): Promise<VoxelChunk[]> {
    const minChunk = this.worldToChunk(minWorldPos);
    const maxChunk = this.worldToChunk(maxWorldPos);

    const chunks = [];

    // Iterate through chunk coordinates
    for (let chunkY = minChunk.y; chunkY <= maxChunk.y; chunkY++) {
      for (let chunkX = minChunk.x; chunkX <= maxChunk.x; chunkX++) {
        chunks.push(this.getOrCreateChunk(chunkX, chunkY, true));
      }
    }

    return await Promise.all(chunks);
  }

  public dispose(): void {
    this.chunks.forEach((chunk) => {
      chunk.voxelStructs?.dispose();
      chunk.voxelStructs = null;
    });
    this.chunks.clear();
    this.terrainGeneratorClient.dispose();
  }
}
